import { BottomBarNavigation, BottomBarNavigationDrawerItemType, Icon, Page } from '@elipssolution/harfang';
import {
	mdiAccount,
	mdiDownload,
	mdiFileDocument,
	mdiFileDocumentOutline,
	mdiFileEye,
	mdiFileEyeOutline,
	mdiLifebuoy,
	mdiLogout,
	mdiScreenRotation,
} from '@mdi/js';
import { Stack, Typography, useTheme } from '@mui/material';
import getConfig from 'next/config';
import { signOut } from 'next-auth/react';
import { ReactNode, useEffect, useState } from 'react';

import PwaInstallPromptDrawer from './PwaInstallPromptDrawer';
import usePWA from '../../../../hooks/usePWA';
import useScreenOrientation from '../../../../hooks/useScreenOrientation';
import modulesConfig from '../../../../modules/modules';
import { ModuleType, NavigationItemType } from '../../../../types/module';
import { ModuleCodeEnum } from '../../../../types/widget';
import { renderFullName } from '../../../../utils/renderFullName';
import useAlert from '../../../hooks/useAlert';
import useCustomRouter from '../../../hooks/useCustomRouter';
import { useModules } from '../../../hooks/useModules';
import { PublicRuntimeConfigType } from '../../../types/publicRuntimeConfig';
import useLocalStorage from '../../../utils/useLocalStorage';
import AlertBanner from '../../AlertBanner';
import MobileCustomerFileSelector from '../../customerFileSelector/MobileCustomerFileSelector';
import { useSession } from '../../session/SessionContext';

const WrongMobileOrientation = () => {
	const {
		publicRuntimeConfig: { appName },
	} = getConfig() as PublicRuntimeConfigType;

	return (
		<Stack justifyContent="center" alignItems="center" flex={1} gap={5}>
			<Icon path={mdiScreenRotation} size={48} />

			<Stack gap={1}>
				<Typography textAlign="center">{appName} n&apos;est pas adapté au format paysage</Typography>
				<Typography textAlign="center"> Merci de repasser en mode portrait</Typography>
			</Stack>
		</Stack>
	);
};

const moduleIconMapper = new Map([
	['document-selected', mdiFileDocument],
	['document', mdiFileDocumentOutline],
	['accounting-selected', mdiFileEye],
	['accounting', mdiFileEyeOutline],
]);

type MobileLayoutContentProps = {
	children: ReactNode;
};

const MobileLayoutContent = ({ children }: MobileLayoutContentProps) => {
	const { isInstalled, isInstallable } = usePWA();
	const screenOrientation = useScreenOrientation();
	const {
		publicRuntimeConfig: { appName, standaloneModule },
	} = getConfig() as PublicRuntimeConfigType;
	const { pathname, push, query } = useCustomRouter();
	const { dossier } = query || {};
	const { user, checkPermission } = useSession();
	const { shape } = useTheme();

	const { alert } = useAlert();
	const { content: alertContent, isActive: isAlertActive } = alert ?? {};

	const modules = useModules();

	const [localStoragePinnedModuleCodes, setLocalStoragePinnedModuleCodes] = useLocalStorage<
		ModuleType['code'][] | undefined
	>('pinnedModuleCodes', undefined);

	const [isInstallPromptDrawerOpen, setIsInstallPromptDrawerOpen] = useState(false);

	const [codeModule] = pathname.split('/').slice(1);
	const selectedModule = codeModule || 'home';

	const isCustomerFileSelectorVisible = !['/support', '/profile'].includes(pathname);

	const bottomNavigationBarItems = standaloneModule
		? []
		: modulesConfig.flatMap(({ code, name, route, permissionCodes }) =>
				permissionCodes?.some(checkPermission) &&
				modules.some(({ code: moduleCode, isAvailableOnMobile }) => moduleCode === code && isAvailableOnMobile)
					? {
							iconPath: moduleIconMapper.get(code) as string,
							iconPathSelected: moduleIconMapper.get(`${code}-selected`) as string,
							code,
							name,
							route,
							isPinned: localStoragePinnedModuleCodes?.includes(code),
					  }
					: [],
		  );

	const navigateToSupport = () => {
		push('/support').catch((error) => {
			throw error;
		});
	};

	const navigateToProfile = () => {
		push('/profile').catch((error) => {
			throw error;
		});
	};

	const logout = () => {
		signOut({ callbackUrl: '/' }).catch((error) => {
			throw error;
		});
	};

	const menuSecondaryItems = [
		{
			iconPath: mdiLifebuoy,
			name: 'Support',
			code: 'support',
			onClick: navigateToSupport,
		},
		{
			code: 'profile',
			iconPath: mdiAccount,
			name: 'Profil',
			onClick: navigateToProfile,
		},
		{
			code: 'logout',
			iconPath: mdiLogout,
			name: 'Se déconnecter',
			onClick: logout,
		},
		...(isInstallable && !isInstalled
			? [
					{
						code: 'install',
						iconPath: mdiDownload,
						name: `Installer ${appName}`,
						onClick: () => setIsInstallPromptDrawerOpen(true),
						variant: 'outlined',
						sx: { borderRadius: shape.borderRadius / 3 },
						color: 'info',
					} as BottomBarNavigationDrawerItemType,
			  ]
			: []),
	];

	const navigateToRoute = ({ route, subItems: propsSubItems }: Pick<NavigationItemType, 'route' | 'subItems'>) => {
		if (isInstallPromptDrawerOpen) {
			setIsInstallPromptDrawerOpen(false);
		}

		const subItems = propsSubItems ?? modulesConfig.find(({ route: moduleRoute }) => moduleRoute === route)?.subItems;

		const firstAllowedSubItem = subItems?.find(({ permissionCodes }) => permissionCodes?.some(checkPermission) ?? true);

		const newRoute = `/${route
			.split('/')
			.concat(firstAllowedSubItem?.route?.replace(/^\//, '') || [])
			.filter((item) => item && (!standaloneModule || item !== standaloneModule))
			.join('/')}`;

		if (firstAllowedSubItem?.subItems) {
			navigateToRoute({ route: newRoute, subItems: firstAllowedSubItem.subItems });

			return;
		}

		push({ pathname: newRoute, query: { dossier } }).catch((error) => {
			throw error;
		});
	};

	const handleModulePinChange = ({ code, isPinned }: { code: string; isPinned: boolean }) => {
		const moduleCode = code as ModuleCodeEnum;

		setLocalStoragePinnedModuleCodes((prevValue = []) =>
			isPinned ? [...prevValue, moduleCode] : prevValue.filter((pinnedModuleCode) => pinnedModuleCode !== code),
		);
	};

	useEffect(() => {
		if (!localStoragePinnedModuleCodes && modules.length > 0) {
			setLocalStoragePinnedModuleCodes(
				modulesConfig.flatMap(({ code, permissionCodes }) =>
					permissionCodes?.some(checkPermission) &&
					modules.some(({ code: moduleCode, isAvailableOnMobile }) => moduleCode === code && isAvailableOnMobile)
						? code
						: [],
				),
			);
		}
	}, [checkPermission, localStoragePinnedModuleCodes, modules, setLocalStoragePinnedModuleCodes]);

	return screenOrientation === 'portrait' ? (
		<>
			{isAlertActive && alertContent && <AlertBanner content={alertContent} />}

			<Page key={pathname} disablePadding={!isCustomerFileSelectorVisible}>
				{isCustomerFileSelectorVisible && <MobileCustomerFileSelector />}

				{children}
			</Page>

			<BottomBarNavigation
				selectedItemCode={selectedModule}
				items={bottomNavigationBarItems}
				onItemClick={navigateToRoute}
				onItemPinChange={handleModulePinChange}
				drawerSecondaryItems={menuSecondaryItems}
				drawerCaption={`Connecté en tant que ${renderFullName(user)}`}
			/>

			{!isInstalled && (
				<PwaInstallPromptDrawer open={isInstallPromptDrawerOpen} onClose={() => setIsInstallPromptDrawerOpen(false)} />
			)}
		</>
	) : (
		<WrongMobileOrientation />
	);
};

export default MobileLayoutContent;
